
import { useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { Home, MessageCircle, Heart, Bell, Menu } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useBadges } from '@/contexts/BadgesContext';
import { UnreadBadge } from './UnreadBadge';
import { Sheet, SheetContent, SheetTrigger, SheetDescription } from '@/components/ui/sheet';
import MobileMenuManager from "@/components/navigation/MobileMenuManager";

const MobileNavigation = () => {
  const location = useLocation();
  const currentRoute = location.pathname;
  const isChatRoute = currentRoute.includes('/chat/');
  const isOnboardingRoute = currentRoute.includes('/onboarding');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  
  const { messagesCount: unreadMessagesCount, notificationsCount } = useBadges();

  // Hide navigation on chat conversation pages and onboarding routes
  if (isChatRoute || isOnboardingRoute) {
    return null;
  }

  const navItems = [
    { 
      icon: Home, 
      label: 'Feed', 
      path: '/main/feed',
    },
    { 
      icon: MessageCircle, 
      label: 'Chats', 
      path: '/main/chats',
      badgeCount: unreadMessagesCount > 0 ? unreadMessagesCount : undefined
    },
    { 
      icon: Heart, 
      label: 'Likes', 
      path: '/main/connections',
    },
    { 
      icon: Bell, 
      label: 'Avisos', 
      path: '/main/notifications',
      badgeCount: notificationsCount > 0 ? notificationsCount : undefined
    }
  ];

  return (
    <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-black border-t border-white/10 select-none pb-[env(safe-area-inset-bottom)]">
      <nav className="grid grid-cols-5 gap-0 px-2">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path || 
            (item.path === '/main/chats' && location.pathname.startsWith('/main/chat/'));
          
          return (
            <Link
              key={item.path}
              to={item.path}
              className={cn(
                "flex flex-col items-center gap-1 py-2 rounded-lg w-full px-1 transition-all duration-200 group relative",
                isActive 
                  ? "bg-luxury-pinkBright text-white shadow-lg" 
                  : "text-gray-300 hover:text-white hover:bg-white/5"
              )}
            >
              <div className="relative">
                <Icon 
                  className={cn(
                    "w-5 h-5 flex-shrink-0 transition-transform duration-200",
                    isActive ? "scale-110" : "group-hover:scale-105"
                  )} 
                />
                {item.badgeCount && (
                  <UnreadBadge 
                    count={item.badgeCount} 
                    size="sm"
                    isActive={isActive}
                  />
                )}
              </div>
              <div className="text-xs font-medium truncate text-center">
                {item.label}
              </div>
            </Link>
          );
        })}

        {/* Menu button - same level as other items */}
        <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
          <SheetTrigger asChild>
            <button 
              className="flex flex-col items-center gap-1 py-2 rounded-lg w-full px-1 text-gray-300 hover:text-white hover:bg-white/5 transition-all duration-200"
              aria-label="Abrir menu"
            >
              <Menu className="w-5 h-5" />
              <span className="text-xs font-medium">Menu</span>
            </button>
          </SheetTrigger>
          <MobileMenuManager 
            isOpen={isMenuOpen} 
            onOpenChange={setIsMenuOpen}
          />
        </Sheet>
      </nav>
    </div>
  );
};

export default MobileNavigation;
